<template>
    <slot
        :logo-color="logoColor"
        :button-color="buttonColor"
        :is-top="isTop"
        :is-open="isOpen"
        :bg-color="bgColor"
        :show-navi="showNavi"
        :toggle-site-nav="toggleSiteNav"
        :toggle="toggleMenu"
        :lang-nav="langNav" />
    <div
        class="fixed left-0 top-0 z-40 h-screen w-full overflow-hidden transition-all duration-700"
        :class="[
            isOpen ? 'max-h-screen' : 'max-h-0',
            contrast ? 'bg-moon text-white' : 'bg-white'
        ]">
        <div
            class="container flex h-full flex-col items-start justify-between overflow-y-auto overflow-x-hidden pb-4 pt-28 lg:overflow-hidden lg:py-40">
            <div class="pointer-events-none absolute hidden h-full lg:block">
                <div
                    class="absolute left-0 top-0 origin-left -translate-x-56 rotate-90 font-serif uppercase text-sun lg:text-huge lg:leading-[0px]">
                    rize
                </div>
            </div>
            <slot
                name="nav"
                :back="backClicked"
                :item="currentItem"
                :show-items="showItems"
                :has-back="hasBack"
                :is-back="isBack"
                :contrast="contrast"
                :navigate="itemClicked" />
            <slot name="teaser" :show="!showSiteNav" />
            <Transition name="slide-bottom-to-top">
                <slot
                    v-if="showSiteNav"
                    name="sitenav"
                    :toggle="toggleSiteNav"
                    :index="siteNavIndex"
                    :select="selectSiteNav" />
            </Transition>
            <div class="flex w-full">
                <button
                    v-if="!showSiteNav && showItems"
                    class="flex items-center lg:hidden"
                    @click="toggleSiteNav">
                    <lz-icon name="sites" class="h-5 w-5 fill-current" />
                    <span class="mx-1">{{ currentSite }}</span>
                    <lz-icon name="chevron" class="h-5 w-5 rotate-90" />
                </button>
                <div
                    v-if="isOpen && langNav.length > 1"
                    class="flex w-full justify-end gap-x-2 lg:hidden">
                    <template v-for="(langItem, index) in langNav" :key="langItem.link">
                        <span
                            v-if="langItem.active"
                            :class="[contrast ? 'text-white' : 'text-moon']"
                            >{{ langItem.label }}</span
                        >
                        <a
                            v-else
                            :href="langItem.link"
                            :class="[
                                contrast
                                    ? 'text-white/50 hover:text-sun'
                                    : 'text-gray-400 hover:text-purple-300'
                            ]">
                            {{ langItem.label.replace(/_.*$/, '') }}
                        </a>
                        <span
                            v-if="index < langNav.length - 1"
                            :class="[contrast ? 'text-white/50' : 'text-moon']"
                            >|</span
                        >
                    </template>
                </div>
            </div>
        </div>
    </div>
    <div class="hidden">
        <slot name="registration" />
    </div>
</template>

<script lang="ts" setup>
import { computed, defineProps, ref } from 'vue';
import { useNavigation } from '@/composables/useNavigation';
import events from '@/events';
import LzIcon from '@/components/base/LzIcon.vue';

interface LangItem {
    label: string;
    link: string;
    active: boolean;
}

const ANIM_TIME = 250;
const props = defineProps<{
    isHome: boolean;
    contrast: boolean;
    currentSite: string;
    langNav: LangItem[];
}>();
// generic navigation logic comes from composable
const { currentItem, hasBack, isTop, isOpen, showNavi, navigate, back, toggle } = useNavigation();

// project specific stuff
const showSiteNav = ref(false);
const siteNavIndex = ref(0);
const showItems = ref(false);
const isBack = ref(false);
const sunrizeSlide = ref(true);
const logoColor = computed(() => {
    if (props.contrast && isOpen.value) {
        return 'text-white';
    }
    if (!isTop.value || isOpen.value) {
        return 'text-black';
    }
    return props.isHome
        ? sunrizeSlide.value
            ? 'text-moon'
            : 'text-white'
        : props.contrast
        ? 'text-white'
        : 'text-black';
});
const buttonColor = computed(() => {
    if (!isTop.value || isOpen.value) {
        return `${
            props.contrast && isOpen.value ? 'text-white lg:text-black' : 'text-black'
        } lg:hover:bg-black lg:hover:text-white lg:hover:border-black`;
    }
    return props.isHome
        ? sunrizeSlide.value
            ? 'text-white border-white lg:text-moon lg:border-moon lg:hover:bg-moon lg:hover:text-white 2xl:hover:bg-white 2xl:hover:text-black 2xl:text-white 2xl:border-white'
            : 'text-black border-black lg:text-white lg:border-white lg:hover:bg-white lg:hover:text-black 2xl:hover:bg-black 2xl:hover:text-white 2xl:text-black 2xl:border-black'
        : props.contrast
        ? 'text-white border-white lg:hover:bg-white lg:hover:text-black'
        : 'text-black border-black lg:hover:bg-black lg:hover:text-white';
});
const bgColor = computed(() => {
    const classes = [
        isTop.value && !isOpen.value
            ? 'bg-transparent'
            : props.contrast && isOpen.value
            ? 'bg-moon'
            : 'bg-white'
    ];
    if (isTop.value || isOpen.value) {
        classes.push('lg:bg-transparent');
    }
    return classes.join(' ');
});

events.$on('stage-slide', ({ sunrize }) => {
    sunrizeSlide.value = sunrize;
});

// temporarily hide nav items for animation
function itemClicked(index: number) {
    isBack.value = false;
    showItems.value = false;
    setTimeout(() => {
        navigate(index);
        showItems.value = true;
    }, ANIM_TIME);
}

function backClicked() {
    isBack.value = true;
    showItems.value = false;
    setTimeout(() => {
        back();
        showItems.value = true;
    }, ANIM_TIME);
}

function toggleSiteNav() {
    isBack.value = showSiteNav.value;
    if (showSiteNav.value) {
        showSiteNav.value = false;
        siteNavIndex.value = 0;
        setTimeout(() => {
            showItems.value = true;
        }, ANIM_TIME);
    } else {
        showItems.value = false;
        setTimeout(() => {
            showSiteNav.value = true;
        }, ANIM_TIME);
    }
}

function selectSiteNav(index: number) {
    siteNavIndex.value = index;
}

function toggleMenu() {
    isBack.value = !isOpen.value;
    if (isOpen.value) {
        showItems.value = false;
        showSiteNav.value = false;
        siteNavIndex.value = 0;
        setTimeout(() => {
            toggle();
            document.body.classList.toggle('overflow-hidden', isOpen.value);
        }, ANIM_TIME);
    } else {
        toggle();
        document.body.classList.toggle('overflow-hidden', isOpen.value);
        setTimeout(() => {
            showItems.value = true;
        }, ANIM_TIME);
    }
}
</script>
